import type { ImgHTMLAttributes, ReactNode } from 'react';
import type { MarketingStockKey } from '../../lib/marketing-stock';
import { stockUrl } from '../../lib/marketing-stock';

type ImgProps = ImgHTMLAttributes<HTMLImageElement>;

export function StockImage({
  stockKey,
  width,
  height,
  alt,
  priority,
  className,
  ...rest
}: {
  stockKey: MarketingStockKey;
  width: number;
  height?: number;
  alt: string;
  priority?: boolean;
  className?: string;
} & Omit<ImgProps, 'src' | 'alt' | 'width' | 'height'>) {
  return (
    <img
      src={stockUrl(stockKey, width, height)}
      alt={alt}
      width={width}
      {...(height != null ? { height } : {})}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      {...(priority ? { fetchPriority: 'high' as const } : { fetchPriority: 'low' as const })}
      className={className}
      {...rest}
    />
  );
}

/** Shared neon-dark backdrop for marketing/public pages. */
export function WarmMarketingBackdrop({ children }: { children: ReactNode }) {
  return (
    <div className="neon-bg relative min-h-screen overflow-hidden">
      <div
        className="neon-mesh pointer-events-none absolute inset-0 opacity-95"
        aria-hidden
      />
      <div
        className="neon-grid pointer-events-none absolute inset-0 opacity-35 motion-safe:animate-pulse"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/60 to-transparent"
        aria-hidden
      />
      <div className="relative text-zinc-100">{children}</div>
    </div>
  );
}

/** Full-bleed strip with image + bottom gradient for text legibility on overlays. */
export function StockHeroStrip({
  stockKey,
  alt,
  className,
  overlayClassName,
}: {
  stockKey: MarketingStockKey;
  alt: string;
  className?: string;
  overlayClassName?: string;
}) {
  return (
    <div
      className={`group relative overflow-hidden rounded-3xl border border-violet-200/60 shadow-xl shadow-violet-900/10 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-violet-900/20 ${className ?? ''}`}
    >
      <StockImage
        stockKey={stockKey}
        width={1600}
        height={520}
        alt={alt}
        className="h-48 w-full object-cover object-center transition-transform duration-700 sm:h-56 md:h-64"
      />
      <div
        className={`pointer-events-none absolute inset-0 bg-gradient-to-t from-violet-950/55 via-transparent to-cyan-50/10 ${overlayClassName ?? ''}`}
        aria-hidden
      />
    </div>
  );
}
